import {
  Stack,
} from '@mui/material';
import Layout from 'components/Layout';
import ScheduleOrderOverlay from 'components/ScheduleOrderOverlay';
import {
  AddressContext, CartContext,
} from 'constants/contexts';
import useHomePath from 'hooks/useHomePath';
import useIsDesktop from 'hooks/useIsDesktop';
import React, {
  useCallback,
  useContext, useEffect, useMemo,
  useState,
} from 'react';

import {
  useParams, useHistory, Route, Switch,
} from 'react-router-dom';

import AlertProvider from 'components/AlertProvider';
import RedirectProvider from 'components/RedirectProvider';
import { env } from 'constants';
import useMerchantPath from 'hooks/useMerchantPath';
import NotFoundPage from 'pages/NotFound';
import { Helmet } from 'react-helmet';
import http from 'services/http';
import { triggerAnalyticEvent } from 'utils/analytics';
import { captureException } from 'utils/error';
import { historyData } from 'utils/history';
import { loadCrispChat } from 'utils/script';
import useMerchant from '../../hooks/useMerchant';
import MerchantItems from './MerchantItems';
import Footer from './Footer';
import Header from './Header';
import Sidebar from './Sidebar';
import Title from './Title';

function MerchantWrapper() {
  const mainRoute = env.IS_MARKETPLACE ? '/place/:merchantId' : '';
  return (
    <Switch>
      <Route path={`${mainRoute}/item/:itemId`} component={Merchant} />
      <Route path={`${mainRoute}/:merchantSlug?`} component={Merchant} />
    </Switch>
  );
}

function Merchant() {
  const subheaderId = 'sub-header';
  const isDesktop = useIsDesktop();
  const { itemId } = useParams();
  const { redirect } = RedirectProvider.useRedirect();
  const cart = useContext(CartContext);
  const { currentAddress, openModal } = useContext(AddressContext);
  const [schedulingOpen, setSchedulingOpen] = useState(false);
  const history = useHistory();
  const homePath = useHomePath();
  const alerts = AlertProvider.useAlerts(AlertProvider.POSITIONS.MDP);

  const handleBack = useCallback(() => {
    const { pathStack } = historyData.get();
    if (['/search', '/feed', '/merchants',
      '/lists'].some((path) => pathStack?.[pathStack.length - 2]?.pathname?.startsWith(path))) {
      return history.goBack();
    }
    return history.push({ pathname: homePath, state: { isBack: true } });
  }, [history, homePath]);

  const { notFound, merchant, fetchedMerchant } = useMerchant();
  const merchantPath = useMerchantPath(merchant);

  const schedulingRequired = useMemo(() => (
    merchant.has_scheduling_order &&
    !merchant.takeout &&
    !cart.scheduledTime &&
    currentAddress &&
    !itemId
  ), [merchant.has_scheduling_order, merchant.takeout, cart.scheduledTime, currentAddress]);

  useEffect(() => {
    if (merchant.id && !window.CRISP_WEBSITE_ID) {
      http.get(`v1/restaurants/${merchant.id}/get_crisp_key/`)
        .then(({ data }) => loadCrispChat(data.data?.crisp_public_key)).catch(captureException);
    }
  }, [merchant.id]);

  useEffect(() => {
    if (schedulingRequired) {
      setSchedulingOpen(true);
    }
  }, [schedulingRequired]);

  useEffect(() => {
    if (!currentAddress && !env.IS_CATERING) {
      openModal();
    }
  }, [currentAddress, openModal, itemId]);

  useEffect(() => {
    if (!fetchedMerchant) {
      return;
    }
    triggerAnalyticEvent('view_merchant', null, {
      migration_driver: fetchedMerchant?.migration_data?.migration_driver,
    });
    if (env.IS_CATERING) {
      cart.updateMerchant({
        onCancel: () => history.replace(merchantPath),
        merchant: fetchedMerchant,
      });
    }
  }, [fetchedMerchant]);

  useEffect(() => {
    if (!fetchedMerchant) {
      return;
    }
    if (!env.IS_CATERING && fetchedMerchant?.is_catering === true) {
      redirect();
      return;
    }
    if (env.IS_CATERING) {
      cart.updateMerchant({
        onCancel: redirect,
        merchant: fetchedMerchant,
      });
    }
  }, [fetchedMerchant, redirect, cart]);

  useEffect(() => {
    if (!itemId || !merchant.id) {
      return;
    }
    cart.updateMerchant({
      onCancel: () => history.replace(merchantPath),
      merchant,
    });
  }, [itemId, merchant, cart, history, merchantPath]);

  if (notFound) {
    return <NotFoundPage />;
  }

  function getHeaderVisibleElements() {
    if (isDesktop) {
      if (env.IS_CATERING) {
        return [
          env.IS_GFO ? Layout.Header.Elements.LOGO : Layout.Header.Elements.TITLE,
          Layout.Header.Elements.USER_BUTTON,
        ];
      }
      return [
        Layout.Header.Elements.LOGO,
        Layout.Header.Elements.ADDRESS_BUTTON,
        Layout.Header.Elements.SEARCH,
        Layout.Header.Elements.USER_BUTTON,
        Layout.Header.Elements.CART_BUTTON,
      ];
    }
    if (env.IS_CATERING) {
      if (env.IS_GFO) {
        return [
          Layout.Header.Elements.LOGO,
          Layout.Header.Elements.USER_BUTTON,
          Layout.Header.Elements.CART_BUTTON,
        ];
      }
      return [
        Layout.Header.Elements.TITLE,
        Layout.Header.Elements.USER_BUTTON,
        Layout.Header.Elements.CART_BUTTON,
      ];
    }
    return [
      Layout.Header.Elements.BACK_BUTTON,
      Layout.Header.Elements.TITLE,
    ];
  }
  return (
    <Layout
      footer={(
        <Footer
          schedulingRequired={schedulingRequired}
          onOpenScheduling={() => {
            setSchedulingOpen(true);
            if (cart.merchant?.id !== merchant?.id) {
              cart.updateMerchant({
                onCancel: () => setSchedulingOpen(false),
                merchant,
              });
            }
          }}
        />
      )}
      header={(
        <>
          <Layout.Header
            visibleElements={getHeaderVisibleElements()}
            title={(
              <Title title={merchant?.name} />
            )}
            onBack={handleBack}
          />
          {/* <Stack gap={1} mb={1}>
            {alerts}
          </Stack> */}
        </>
      )}
      sidebar={(
        <Sidebar
          setSchedulingOpen={setSchedulingOpen}
          schedulingRequired={schedulingRequired}
        />
      )}
      sidebarWidth="420px"
      headerHeight={isDesktop ? 84 : 64}
    >
      <Helmet>
        <title>
          Online order from
          {' '}
          {merchant?.name || ''}
        </title>
        {
          merchant?.slug && (
            <link rel="canonical" href={`${window.location.origin}${env.IS_CATERING ? '' : '/pwa'}${merchantPath}`} />
          )
        }
      </Helmet>
      <Stack
        className="merchant"
        sx={{
          flex: 1,
          maxWidth: '100%',
        }}
        spacing={1}
        pb={{
          xs: 8,
          md: 4,
        }}
      >
        <Header
          merchant={merchant}
          loading={!merchant?.id}
          subheaderId={subheaderId}
        />
        <MerchantItems
          merchant={merchant}
          openScheduling={() => setSchedulingOpen(true)}
          subheaderId={subheaderId}
        />
      </Stack>
      {
        !env.IS_CATERING && merchant?.has_scheduling_order && !merchant?.is_disabled && (
          <ScheduleOrderOverlay
            merchant={merchant}
            open={schedulingOpen}
            onClose={() => setSchedulingOpen(false)}
          />
        )
      }
    </Layout>
  );
}

export default MerchantWrapper;
